import { useCallback, useSyncExternalStore } from 'react';

import { getUserStorageOwner, userStorage } from '../utils/userStorage';

const STORAGE_KEY = 'selectedAppId';

const createStore = () => {
  let listeners: Array<() => void> = [];
  let currentValue: string | null = null;
  let currentOwner: string | null | undefined;

  const getSnapshot = () => {
    const owner = getUserStorageOwner();
    if (owner !== currentOwner || currentValue === null) {
      currentOwner = owner;
      const stored = userStorage.getItem(STORAGE_KEY);
      currentValue = stored ? JSON.parse(stored) : undefined;
    }
    return currentValue;
  };

  const subscribe = (listener: () => void) => {
    listeners.push(listener);
    return () => {
      listeners = listeners.filter((l) => l !== listener);
    };
  };

  const setValue = (value: string | undefined) => {
    currentOwner = getUserStorageOwner();
    currentValue = value ?? null;
    if (value === undefined) {
      userStorage.removeItem(STORAGE_KEY);
    } else {
      userStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    }
    listeners.forEach((listener) => listener());
  };

  const clearValue = () => {
    currentOwner = getUserStorageOwner();
    currentValue = null;
    userStorage.removeItem(STORAGE_KEY);
    listeners.forEach((listener) => listener());
  };

  return { getSnapshot, subscribe, setValue, clearValue };
};

const store = createStore();

/** Module-level clear so group-store can reset us without hook coupling. */
export const clearSelectedApplicationIdStore = () => store.clearValue();

export const useSelectedApplicationId = () => {
  const selectedApplicationId = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot
  );

  const setSelectedApplicationId = useCallback((appId: string | undefined) => {
    store.setValue(appId);
  }, []);

  const clearSelectedApplicationId = useCallback(() => {
    store.clearValue();
  }, []);

  return {
    selectedApplicationId,
    setSelectedApplicationId,
    clearSelectedApplicationId,
  };
};

export default useSelectedApplicationId;
